import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import {
  Users as UsersIcon,
  Search,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Download
} from 'lucide-react'
import { SkeletonTable } from '../components/Skeleton'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'

const Users = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [usersPerPage] = useState(20)
  const [confirmUser, setConfirmUser] = useState(null)

  const { getUsers, deleteUser } = useFirebase()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      console.log('🔄 Loading users data...')
      const usersData = await getUsers()
      console.log('📊 Users data received:', usersData)
      setUsers(usersData || [])
    } catch (error) {
      console.error('❌ Error loading data:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleUserClick = (userId) => {
    navigate(`/users/details/${userId}`)
  }

  const handleDelete = (id) => {
    const u = users.find(x => x.id === id) || { id }
    setConfirmUser({ id: u.id, name: u.name, email: u.email })
  }

  const confirmDelete = async () => {
    if (!confirmUser) return
    try {
      await deleteUser(confirmUser.id)
      setUsers(prev => prev.filter(u => u.id !== confirmUser.id))
      toast.success('User deleted successfully')
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error('Failed to delete user')
    } finally {
      setConfirmUser(null)
    }
  }

  const cancelDelete = () => setConfirmUser(null)

  const handleSearch = () => {
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchTerm('')
    setStartDate('')
    setEndDate('')
    setCurrentPage(1)
  }

  const handleExport = () => {
    if (filteredUsers.length === 0) {
      toast.error('No users to export')
      return
    }

    // Prepare data for Excel
    const exportData = filteredUsers.map((user, index) => ({
      'Sr. No.': index + 1,
      'Name': user.name || 'N/A',
      'Email': user.email || 'N/A',
      'Mobile': user.mobileNumber || 'N/A',
      'Country': user.country || 'N/A',
      'State': user.state || 'N/A',
      'District': user.district || 'N/A',
      'Registration Date': user.createdAt
        ? new Date(user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt).toLocaleDateString('en-GB')
        : 'N/A'
    }))

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData)

    // Set column widths
    worksheet['!cols'] = [
      { wch: 8 },  // Sr. No.
      { wch: 20 }, // Name
      { wch: 30 }, // Email
      { wch: 15 }, // Mobile
      { wch: 15 }, // Country
      { wch: 15 }, // State
      { wch: 15 }, // District
      { wch: 18 }  // Registration Date
    ]

    // Create workbook
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users')

    // Generate filename with date range if applicable
    let filename = 'users'
    if (startDate && endDate) {
      filename += `_${startDate}_to_${endDate}`
    } else if (startDate) {
      filename += `_from_${startDate}`
    } else if (endDate) {
      filename += `_until_${endDate}`
    }
    filename += '.xlsx'

    // Download file
    XLSX.writeFile(workbook, filename)
    toast.success(`Exported ${filteredUsers.length} users successfully!`)
  }

  // Filter users
  const filteredUsers = users.filter(user => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      const matchesSearch = (
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.country?.toLowerCase().includes(searchLower) ||
        user.state?.toLowerCase().includes(searchLower) ||
        user.district?.toLowerCase().includes(searchLower) ||
        user.mobileNumber?.toLowerCase().includes(searchLower)
      )
      if (!matchesSearch) return false
    }

    // Date filter
    if (startDate || endDate) {
      const userDate = user.createdAt
        ? new Date(user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt)
        : null

      if (!userDate) return false

      // Reset time to start of day for accurate comparison
      userDate.setHours(0, 0, 0, 0)

      if (startDate) {
        const start = new Date(startDate)
        start.setHours(0, 0, 0, 0)
        if (userDate < start) return false
      }

      if (endDate) {
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)
        if (userDate > end) return false
      }
    }

    return true
  })

  // Sort by registration date descending (newest first)
  const sortedUsers = filteredUsers.sort((a, b) => {
    const getTime = (date) => {
      if (!date) return 0
      if (date.seconds) return date.seconds * 1000
      return new Date(date).getTime()
    }
    return getTime(b.createdAt) - getTime(a.createdAt)
  })

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage
  const indexOfFirstUser = indexOfLastUser - usersPerPage
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser)
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage)


  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Gradient Background */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 rounded-xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UsersIcon className="h-8 w-8 text-white" />
            <div>
              <h1 className="text-3xl font-bold">User Management</h1>
              <p className="text-white/90 text-lg mt-1">
                Monitor and manage nursing exam application users
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg border border-white/20">
            <UsersIcon className="h-5 w-5 text-white/80" />
            <span className="text-white/90 font-medium">
              {users.length} Total Users
            </span>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <label htmlFor="search" className="form-label">
                Search Users
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  id="search"
                  className="form-input pl-10"
                  placeholder="Search by name, email, mobile, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Date Filters and Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="startDate" className="form-label">
                  Start Date:
                </label>
                <input
                  type="date"
                  id="startDate"
                  className="form-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="flex-1">
                <label htmlFor="endDate" className="form-label">
                  End Date:
                </label>
                <input
                  type="date"
                  id="endDate"
                  className="form-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="flex items-end space-x-2">
                <button
                  onClick={handleExport}
                  className="btn-primary flex items-center gap-2 whitespace-nowrap"
                  disabled={filteredUsers.length === 0}
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
                <button onClick={clearSearch} className="btn-outline whitespace-nowrap">
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="card">
        {loading ? (
          <div className="p-6"><SkeletonTable rows={10} columns={6} /></div>
        ) : (
          <>
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                Users ({sortedUsers.length} total)
              </h3>
            </div>

            <div className="card-body">
              {currentUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Country</th>
                        <th>State</th>
                        <th>District</th>
                        <th>Password</th>
                        <th>Registration Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleUserClick(user.id)}
                        >
                          <td>
                            <div className="font-medium text-gray-900">
                              {user.name || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="text-sm text-gray-600">
                              {user.email || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="text-sm text-gray-600">
                              {user.mobileNumber || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="text-sm text-gray-600">
                              {user.country || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="text-sm text-gray-600">
                              {user.state || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="text-sm text-gray-600">
                              {user.district || 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="text-sm text-gray-600 font-mono">
                              {user.password ? '••••••••' : 'N/A'}
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center text-sm text-gray-600">
                              <Calendar className="h-4 w-4 mr-1" />
                              {user.createdAt
                                ? new Date(user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt).toLocaleDateString('en-GB')
                                : 'N/A'
                              }
                            </div>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/users/view/${user.id}`}
                                className="text-blue-600 hover:text-blue-900"
                                title="View user details"
                              >
                                <Eye className="h-4 w-4" />
                              </Link>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDelete(user.id)
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Delete user"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No users found
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm
                      ? 'Try adjusting your search criteria.'
                      : 'No users have registered for your app yet.'
                    }
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, sortedUsers.length)} of {sortedUsers.length} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn-outline disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + Math.max(1, currentPage - 2)
              if (page > totalPages) return null

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {page}
                </button>
              )
            })}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn-outline disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {confirmUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelDelete} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="text-lg font-semibold text-gray-900 mb-2">Delete user?</div>
            <div className="text-sm text-gray-600 mb-6">
              This action cannot be undone. Remove
              {` ${confirmUser.name || confirmUser.email || confirmUser.id} `}
              and associated data from Firestore.
            </div>
            <div className="flex justify-end space-x-2">
              <button onClick={cancelDelete} className="btn-outline">Cancel</button>
              <button onClick={confirmDelete} className="btn-danger">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Users
